import { useState, useEffect } from 'react';
import { MetricsChart } from '../../../components/observability/MetricsChart';
import { observabilityService, type MetricResult } from '../../../services/observability.service';
import { useTenantStore } from '../../../stores/tenantStore';

interface EdgeHealthChartsProps {
  edgeId: string;
}

const COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const EdgeHealthCharts = ({ edgeId }: EdgeHealthChartsProps) => {
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const [loading, setLoading] = useState(true);
  const [cpuHistoricalData, setCpuHistoricalData] = useState<any[]>([]);
  const [memoryData, setMemoryData] = useState<any[]>([]);
  const [netRxData, setNetRxData] = useState<any[]>([]);
  const [netTxData, setNetTxData] = useState<any[]>([]);

  const transformMetricData = (results: MetricResult[], keyName: string) => {
    if (!results || results.length === 0) return [];
    const dataMap = new Map<number, any>();
    results.forEach((series) => {
      const seriesLabel = series.metric.interface ? `${keyName}_${series.metric.interface}` : keyName;
      series.values?.forEach(([ts, val]) => {
        if (!dataMap.has(ts)) dataMap.set(ts, { timestamp: ts });
        const point = dataMap.get(ts);
        point[seriesLabel] = parseFloat(val);
      });
    });
    return Array.from(dataMap.values()).sort((a, b) => a.timestamp - b.timestamp);
  };

  const getDynamicLines = (data: any[], excludeKeys = ['timestamp']) => {
    if (!data || data.length === 0) return [];
    const keys = Object.keys(data[0] || {}).filter(k => !excludeKeys.includes(k));
    return keys.map((k, i) => ({
      dataKey: k,
      name: k.replace(/_/g, ' '),
      color: COLORS[i % COLORS.length]
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!edgeId) return;
      setLoading(true);
      try {
        const end = Math.floor(Date.now() / 1000);
        const start = end - 3600; // Last 1 hour
        const step = 60;

        const [cpuRes, memRes, netRxRes, netTxRes] = await Promise.all([
          observabilityService.queryRange(`edge_cpu_usage_percent{edge_id="${edgeId}"}`, start, end, step),
          observabilityService.queryRange(`edge_memory_bytes{edge_id="${edgeId}"} / 1024 / 1024`, start, end, step),
          observabilityService.queryRange(`rate(edge_interface_rx_bytes_total{edge_id="${edgeId}"}[5m])`, start, end, step),
          observabilityService.queryRange(`rate(edge_interface_tx_bytes_total{edge_id="${edgeId}"}[5m])`, start, end, step),
        ]);

        if (cpuRes.status === 'success') setCpuHistoricalData(transformMetricData(cpuRes.data.result, 'CPU Usage'));
        if (memRes.status === 'success') setMemoryData(transformMetricData(memRes.data.result, 'Memory Usage'));
        if (netRxRes.status === 'success') setNetRxData(transformMetricData(netRxRes.data.result, 'RX'));
        if (netTxRes.status === 'success') setNetTxData(transformMetricData(netTxRes.data.result, 'TX'));
      } catch (error) {
        console.error('Failed to fetch metrics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [edgeId, selectedTenantId]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MetricsChart
          title="CPU Performance"
          data={cpuHistoricalData}
          loading={loading}
          unit="%"
          lines={[{ dataKey: 'CPU Usage', name: 'CPU Usage', color: COLORS[0] }]}
        />

        <MetricsChart
          title="Memory Utilization"
          data={memoryData}
          loading={loading}
          unit=" MB"
          lines={[{ dataKey: 'Memory Usage', name: 'Memory Usage', color: COLORS[1] }]}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MetricsChart
          title="Network Ingress (RX)"
          data={netRxData}
          loading={loading}
          unit=" B/s"
          lines={getDynamicLines(netRxData)}
        />

        <MetricsChart
          title="Network Egress (TX)"
          data={netTxData}
          loading={loading}
          unit=" B/s"
          lines={getDynamicLines(netTxData)}
        />
      </div>
    </div>
  );
};

export default EdgeHealthCharts;
